import { useRequest } from '@mgm/common/lib';
import { TAxiosError } from '@mgm/common/types';
import { CONTROLLERS } from '../utils/enums';
import {
  TAssignBulk, TAssignMappingReq, TExchangeVariantReq, TGetShopifyOrdersReq, TReassignMappingReq,
} from '../types/requests';
import { TCommissionPlan } from '../types';
import { BASE_URL } from '../../config';
import { getIdToken } from '../utils/helpers';

export function OrdersController() {
  const { axiosRequest, initRequestOptions } = useRequest(BASE_URL);

  const controllerName = CONTROLLERS.ORDERS;

  const getShopifyOrders = <T>(data: TGetShopifyOrdersReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/get_orders`, options);
  };
  const getOrderFirstPage = <T>(data: TGetShopifyOrdersReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/first_page`, options);
  };

  const assignBulk = <T>(data: TAssignBulk): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/assign`, options);
  };

  const assignOrders = <T>(data: {
    shopName: string;
    monthYear: string,
    orders: { [key: string]: Array<{
      orderId: string;
      commissionPlan?: TCommissionPlan;
      team?: string;
    }> }
  }): Promise<{ success: boolean; error: TAxiosError } | T> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/assign_mappings`, options);
  };

  const reassignOrder = <T>(data: {
    shopName: string;
    orderData: {
      orderId: string;
      email: string;
      team?: string;
      shopifyId?: string;
    }
  }): Promise<{ success: boolean; error: TAxiosError } | T> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/reassign_order`, options);
  };

  const recalculatePlan = <T>(data: {
    shopName: string,
    commissionPlanId: number,
    orderIds: Array<string> }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/recalculate_plan`, options);
  };

  const getOrdersMapping = <T>(data: {
    shopName: string,
    monthYears: Array<string> }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/monthYear_mappings`, options);
  };

  const reassignMapping = <T>(data: TReassignMappingReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/reassign_mapping`, options);
  };

  const assignMappings = <T>(data: TAssignMappingReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/assign_mappings`, options);
  };

  const getOrdersCount = <T>(data: {
    shopName: string,
    from: string,
    to: string,
    timezone: string
  }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/count`, options);
  };

  const exchangeVariant = <T>(data: TExchangeVariantReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/exchange_variant`, options);
  };

  return {
    getShopifyOrders,
    getOrderFirstPage,
    assignBulk,
    assignOrders,
    recalculatePlan,
    reassignOrder,
    getOrdersMapping,
    reassignMapping,
    getOrdersCount,
    assignMappings,
    exchangeVariant,
  };
}
